import { useFormikContext } from "formik";
import React, { FC, useEffect, useState } from "react";
import { FILTER_BUILDINGS, FILTER_DEVICE_TYPE, FILTER_DOORS, FILTER_SITES, FILTER_ZONES } from "../../../../constants/notification-key";
import { TranslationKeys } from "../../../../contexts/TranslationKeys";
import { useLocale } from "../../../../contexts/localeContext";
import { EventType } from "../../../../models/EventType";
import { getBuildingsData, getDevicesData, getDoorsData, getEventsData, getRecipientData, getSitesData, getZonesData } from "../../../../utilities/NotificationsUtils";
import ActivityFrame from "../../../activity-frame/activity-frame";

interface SummaryProps {
    onBack: (event: any) => void
    taskDescription: string;
}

const Summary: FC<SummaryProps> = ({ onBack, taskDescription }) => {
    const { translate } = useLocale();
    const { values } = useFormikContext<any>();
    const [sendToMasterData, setSendToMasterData] = useState<any>([]);
    const [copyToMasterData, setCopyToMasterData] = useState<any>([]);
    const [filterOptionObjs, setFilterOptionObjs] = useState<any>([]);
    const [events, setEvents] = useState<EventType[]>([])
    const [busy, setBusy] = useState<boolean>(false);
    const [filterValue, setFilterValue] = useState<string>('')

    useEffect(() => {
        setData();
    }, []);

    const setData = async () => {
        setBusy(true);

        const { toRecipientsData, ccRecipientsData } = await getRecipientData(translate, values.toRecipients, values.ccRecipients);
        setSendToMasterData(toRecipientsData);
        setCopyToMasterData(ccRecipientsData);
        setEvents(await getEventsData(values.eventSelectionId, translate));

        const configurationId = values.configurationId;
        switch (values.filterById) {
            case FILTER_SITES.sdValstring:
                setFilterValue(translate.byKey(TranslationKeys.site));
                setFilterOptionObjs(await getSitesData(configurationId));
                break;
            case FILTER_BUILDINGS.sdValstring:
                setFilterValue(translate.byKey(TranslationKeys.building));
                setFilterOptionObjs(await getBuildingsData(configurationId));
                break;
            case FILTER_ZONES.sdValstring:
                setFilterValue(translate.byKey(TranslationKeys.zone));
                setFilterOptionObjs(await getZonesData(configurationId));
                break;
            case FILTER_DOORS.sdValstring:
                setFilterValue(translate.byKey(TranslationKeys.door));
                setFilterOptionObjs(await getDoorsData(configurationId));
                break;
            case FILTER_DEVICE_TYPE.sdValstring:
                setFilterValue(translate.byKey(TranslationKeys.deviceType));
                setFilterOptionObjs(await getDevicesData(configurationId));
                break;
            default:
                throw new Error("Invalid filter option");
        }
        setBusy(false);
    };

    return (
        <ActivityFrame busy={busy} className='flex flex-col flex-grow bg-white h-full'>
            {/* page title */}
            <h1 className={`mb-3 text-portal-blue-primary-shade2 text-xl font-semibold`}>
                {translate.byKey(TranslationKeys.summary)}
            </h1>

            <div className="overflow-y-auto overflow-x-hidden" style={{ height: '380px' }}>
                {/* notification type */}
                <div className="flex pb-3">
                    <div className="w-1/3">
                        <h4 className="p-1 text-xs text-gray-600">
                            {translate.byKey(TranslationKeys.selectNotificationType)}:
                        </h4>
                    </div>
                    <div className="w-2/3 mt-1">
                        <div className="text-sm text-black">
                            {taskDescription}
                        </div>
                    </div>
                </div>
                <hr />

                {/* notification name */}
                <div className="flex pb-3 mt-1">
                    <div className="w-1/3">
                        <h4 className="p-1 text-xs text-gray-600">
                            {translate.byKey(TranslationKeys.setNotificationName)}:
                        </h4>
                    </div>
                    <div className="w-2/3 mt-1">
                        <div className="text-sm text-black">
                            {values.name}
                        </div>
                    </div>
                </div>
                <hr />

                {/* Select the Event */}
                <div className="flex pb-3 mt-1">
                    <div className="w-1/3">
                        <h4 className="p-1 text-xs text-gray-600">
                            {translate.byKey(TranslationKeys.selectTheEvent)}:
                        </h4>
                    </div>
                    <div className="w-2/3 mt-1">
                        {events.map((event) => {
                            return (
                                <div key={event.id} className="text-sm text-black">
                                    {event.name}
                                </div>
                            )
                        })}
                    </div>
                </div>
                <hr />

                {/* Filter by */}
                <div className="flex pb-3 mt-1">
                    <div className="w-1/3">
                        <h4 className="p-1 text-xs text-gray-600">
                            {translate.byKey(TranslationKeys.filterBy)}:
                        </h4>
                    </div>
                    <div className="w-2/3 mt-1">
                        <div className="text-sm text-black">
                            {filterValue}
                        </div>
                    </div>
                </div>
                <hr />

                {/* Device */}
                <div className="flex pb-3 mt-1">
                    <div className="w-1/3">
                        <h4 className="p-1 text-xs text-gray-600">
                            {filterValue}:
                        </h4>
                    </div>
                    <div className="w-2/3 mt-1">
                        {filterOptionObjs && filterOptionObjs.map(item => {
                            return (
                                <div key={item.id} className="text-sm text-black">
                                    {item.name}
                                </div>
                            )
                        })}
                    </div>
                </div>
                <hr />

                {/* send to recipients */}
                <div className="flex pb-3 mt-1">
                    <div className="w-1/3">
                        <h4 className="p-1 text-xs text-gray-600">
                            {translate.byKey(TranslationKeys.sendToRecipient)}:
                        </h4>
                    </div>
                    <div className="w-1/3 mt-1">
                        {sendToMasterData && sendToMasterData.map((data) => {
                            return (
                                <div
                                    className="text-sm text-black"
                                    key={data.id}
                                >
                                    {data.name}
                                </div>
                            );
                        })}
                    </div>
                    <div className="w-1/3 mt-1">
                        {sendToMasterData && sendToMasterData.map((data) => {
                            return (
                                <div
                                    className="text-sm text-black"
                                    key={data.id}
                                >
                                    {data.email}
                                </div>
                            );
                        })}
                    </div>
                </div>
                <hr />

                {/* cc recipients */}
                <div className="flex pb-3 mt-1">
                    <div className="w-1/3">
                        <h4 className="p-1 text-xs text-gray-600">
                            {translate.byKey(TranslationKeys.copyToRecipient)}:
                        </h4>
                    </div>
                    <div className="w-1/3 mt-1">
                        {copyToMasterData && copyToMasterData.map((data) => {
                            return (
                                <div
                                    className="text-sm text-black"
                                    key={data.id}
                                >
                                    {data.name}
                                </div>
                            );
                        })}
                    </div>
                    <div className="w-1/3  mt-1">
                        {copyToMasterData && copyToMasterData.map((data) => {
                            return (
                                <div
                                    className="text-sm text-black"
                                    key={data.id}
                                >
                                    {data.email}
                                </div>
                            );
                        })}
                    </div>
                </div>
                <hr />

                {/* email template */}
                <div className="flex pb-3 mt-1">
                    <div className="w-1/3">
                        <h4 className="p-1 text-xs text-gray-600">
                            {translate.byKey(TranslationKeys.emailTemplate)}:
                        </h4>
                    </div>
                    <div className="w-2/3 mt-1">
                        <div className="flex-column">
                            <h1 className="text-sm text-black font-bold">
                                {translate.byKey(TranslationKeys.emailSubject)}:
                            </h1>
                            <div className="text-sm text-black">
                                {values.subject}
                            </div>

                            {values.body &&
                                <div className="flex-column mt-3">
                                    <h1 className="text-sm text-black font-bold">
                                        {translate.byKey(TranslationKeys.emailBody)}:
                                    </h1>
                                    <div className="text-sm text-black">
                                        {values.body}
                                    </div>
                                </div>
                            }

                            {values.disclaimer &&
                                <div className="flex-column mt-3">
                                    <h1 className="text-sm text-black font-bold">
                                        {translate.byKey(TranslationKeys.disclaimer)}:
                                    </h1>
                                    <div className="text-sm text-black">
                                        {values.disclaimer}
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-row justify-end absolute bottom-0 right-0 pr-3 pb-3 ">
                <div className="flex flex-row mt-4">
                    <button
                        className="py-1 px-5 secondary secondary-inverse"
                        type="button" onClick={onBack}>{translate.byKey(TranslationKeys.back)}</button>
                </div>

                <div className="flex flex-row justify-end mt-4 pl-3">
                    <button
                        className={`py-1 px-5 secondary secondary-enabled`}
                        type="submit">
                        {translate.byKey(TranslationKeys.confirm)}
                    </button>
                </div>
            </div>
        </ActivityFrame>
    )
}

export default Summary;
